'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { createClient } from '@/lib/supabase/client'

export default function SignupPage() {
  const [step, setStep] = useState(1)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [shopName, setShopName] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const router = useRouter()
  const supabase = createClient()

  const validateSubdomain = (domain: string) => {
    const regex = /^(?!-)[a-z0-9-]{3,30}(?<!-)$/
    return regex.test(domain)
  }

  const handleAuthSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (password !== confirmPassword) {
      setError('パスワードが一致しません')
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError('パスワードは6文字以上で入力してください')
      setLoading(false)
      return
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: undefined, // メール確認を無効化
        }
      })

      if (error) {
        console.error('Auth signup error:', error)
        setError('認証エラー: ' + error.message + ' (詳細: ' + JSON.stringify(error) + ')')
        return
      }

      if (data.user) {
        setStep(2)
      }
    } catch (err) {
      setError('アカウント作成に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleShopCreation = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!validateSubdomain(subdomain)) {
      setError('サブドメインは3-30文字の英数字とハイフンのみで、ハイフンで始まり終わることはできません')
      setLoading(false)
      return
    }

    try {
      // サブドメインの重複チェック
      const { data: existingShop } = await supabase
        .from('shops')
        .select('id')
        .eq('subdomain', subdomain)
        .single()

      if (existingShop) {
        setError('このサブドメインは既に使用されています')
        setLoading(false)
        return
      }

      // 現在のユーザーを取得
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        setError('認証エラーが発生しました')
        setLoading(false)
        return
      }

      // 店舗を作成
      const { data: shop, error: shopError } = await supabase
        .from('shops')
        .insert([{
          name: shopName,
          subdomain: subdomain,
          contract_status: 'trial'
        }])
        .select()
        .single()

      if (shopError) {
        console.error('Shop creation error:', shopError)
        setError('店舗作成に失敗しました: ' + shopError.message + ' (詳細: ' + JSON.stringify(shopError) + ')')
        setLoading(false)
        return
      }

      // ユーザーを作成
      const { error: userError } = await supabase
        .from('users')
        .insert([{
          shop_id: shop.id,
          auth_user_id: user.id,
          email: user.email!,
          role: 'manager',
          is_active: true
        }])

      if (userError) {
        console.error('User creation error:', userError)
        setError('ユーザー作成に失敗しました: ' + userError.message + ' (詳細: ' + JSON.stringify(userError) + ')')
        setLoading(false)
        return
      }

      // ダッシュボードにリダイレクト
      router.push(`/shop/${shop.id}/dashboard`)
    } catch (err) {
      console.error('Signup error:', err)
      setError('店舗作成に失敗しました: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="h-12 w-12 rounded-lg bg-navy-600"></div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            新規登録
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            既にアカウントをお持ちの場合は{' '}
            <Link href="/login" className="font-medium text-primary hover:text-primary/80">
              ログイン
            </Link>
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 ? 'アカウント作成' : '店舗情報入力'}
            </CardTitle>
            <CardDescription>
              {step === 1
                ? 'メールアドレスとパスワードを設定してください'
                : '店舗の基本情報を入力してください'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <form onSubmit={handleAuthSignup} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    メールアドレス
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1"
                    placeholder="example@example.com"
                    autoComplete="username"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    パスワード
                  </label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1"
                    placeholder="6文字以上"
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    パスワード（確認）
                  </label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="mt-1"
                    placeholder="パスワードを再入力"
                    autoComplete="new-password"
                  />
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? '作成中...' : 'アカウント作成'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleShopCreation} className="space-y-4">
                <div>
                  <label htmlFor="shopName" className="block text-sm font-medium text-gray-700">
                    店舗名
                  </label>
                  <Input
                    id="shopName"
                    type="text"
                    value={shopName}
                    onChange={(e) => setShopName(e.target.value)}
                    required
                    className="mt-1"
                    placeholder="例: 新宿店"
                  />
                </div>

                <div>
                  <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700">
                    サブドメイン
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      your-shop.
                    </span>
                    <Input
                      id="subdomain"
                      type="text"
                      value={subdomain}
                      onChange={(e) => setSubdomain(e.target.value)}
                      required
                      className="rounded-l-none"
                      placeholder="shinjuku"
                    />
                    <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      .example.com
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    3-30文字の英数字とハイフンのみ。ハイフンで始まり終わることはできません
                  </p>
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                    {error}
                  </div>
                )}

                <div className="flex space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setStep(1)}
                  >
                    戻る
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={loading}
                  >
                    {loading ? '作成中...' : '店舗作成'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="text-center text-xs text-gray-500">
          登録することで、利用規約およびプライバシーポリシーに同意したものとみなされます。
        </div>
      </div>
    </div>
  )
}
